import { useState, useMemo, useCallback } from 'react';
import type { Message, MessageFilters, FilterType } from '../types';

export function useMessageFilters(messages: Message[]) {
    const [filters, setFilters] = useState<MessageFilters>({
        type: 'all',
        search: '',
    });

    const setFilterType = useCallback((type: FilterType) => {
        setFilters(prev => ({ ...prev, type }));
    }, []);

    const setSearch = useCallback((search: string) => {
        setFilters(prev => ({ ...prev, search }));
    }, []);

    const filteredMessages = useMemo(() => {
        return messages.filter(msg => {
            if (filters.type === 'unread' && msg.status !== 'unread') return false;
            if (filters.type === 'archived' && msg.status !== 'archived') return false;
            if (filters.type !== 'all' && filters.type !== 'unread' && filters.type !== 'archived') {
                if (msg.kind !== filters.type) return false;
            }

            if (filters.search) {
                const searchLower = filters.search.toLowerCase();
                const matchesSearch = 
                    msg.title.toLowerCase().includes(searchLower) ||
                    msg.body.toLowerCase().includes(searchLower) ||
                    msg.metadata?.siteName?.toLowerCase().includes(searchLower);
                if (!matchesSearch) return false;
            }

            return true;
        });
    }, [messages, filters]);

    const hasActiveFilters = filters.type !== 'all' || filters.search.length > 0;

    const clearFilters = useCallback(() => {
        setFilters({ type: 'all', search: '' });
    }, []);

    return {
        filters,
        setFilterType,
        setSearch,
        filteredMessages,
        hasActiveFilters,
        clearFilters,
    };
}